/**
 * Bar Chart Component
 */

import { IBuilderComponent } from '../../base'
import type {
  ResponsiveValue,
  SpacingInputValue,
  TypographyValue,
  VariableTextValue,
} from '../../types'
import {
  formatComponentAlignmentFormat,
  formatFromVariableText,
  formatSpacingFormat,
  formatTypographyFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseTypographyFormat,
  parseToVariableText,
  parseWidthFormat,
} from '../../parser-utils'
import barChartTemplate from './bar-chart.template.json'

const BAR_CHART_DEFAULT_TEMPLATE = barChartTemplate as unknown as IBarChartComponentSettings

export interface IBarChartComponentSettings {
  // Data settings
  'bar-chart-data-source': string
  'bar-chart-data-x-axis-column': string
  'bar-chart-data-value-columns': string[]
  'bar-chart-data-heading': VariableTextValue
  'bar-chart-data-sort-by'?: string
  'bar-chart-data-sort-type'?: string
  'bar-chart-data-filter'?: any
  'bar-chart-data-items-per-page'?: number[]

  // Design settings
  'bar-chart-design-alignment'?: ResponsiveValue<string>
  'bar-chart-design-heading-alignment'?: ResponsiveValue<string>
  'bar-chart-design-color-sequence'?: string
  'bar-chart-design-width'?: ResponsiveValue<string[] | number[]>
  'bar-chart-design-typography'?: ResponsiveValue<TypographyValue>
  'bar-chart-design-heading-typography'?: TypographyValue
  'bar-chart-design-x-axis-typography'?: TypographyValue
  'bar-chart-design-values-typography'?: TypographyValue
  'bar-chart-design-legend'?: boolean
  'bar-chart-design-show-grid'?: boolean
  'bar-chart-design-show-axis'?: boolean
  'bar-chart-design-horizontal'?: string
  'bar-chart-design-style'?: string
  'bar-chart-design-heading-margin'?: ResponsiveValue<SpacingInputValue>
  'bar-chart-design-comp-margin'?: ResponsiveValue<SpacingInputValue>
  'bar-chart-design-legend-gap'?: SpacingInputValue
}

export interface IBarChartComponent extends IBuilderComponent {
  componentId: 'bar'
  groupId: 'chart'
  settings: IBarChartComponentSettings
}

export interface AiBarChartInput {
  dataSource: string
  xAxisColumn?: string
  valueColumns?: string | string[]
  heading?: string[] // Format: ["text:Sales Chart"] or ["text:Revenue by ", "cr:category"]
  sortBy?: string
  sortType?: string
  filter?: any
  itemsPerPage?: number
  width?: string
  alignment?: string
  headingAlignment?: string
  margin?: string
  headingMargin?: string
  typography?: string
  headingTypography?: string
  colorSequence?: string
  legend?: boolean
  showGrid?: boolean
  showAxis?: boolean
  horizontal?: string
  style?: string
}

export class BarChartComp {
  getTemplate(): IBarChartComponentSettings {
    return BAR_CHART_DEFAULT_TEMPLATE
  }

  /**
   * format bar chart component settings to AI bar chart input
   */
  format(settings: IBarChartComponentSettings): AiBarChartInput {
    const template = this.getTemplate()
    const result: AiBarChartInput = {
      dataSource: settings['bar-chart-data-source'],
    }

    // X Axis Column
    if (settings['bar-chart-data-x-axis-column']) {
      result.xAxisColumn = settings['bar-chart-data-x-axis-column']
    }

    // Value Columns
    if (settings['bar-chart-data-value-columns'] && settings['bar-chart-data-value-columns'].length > 0) {
      if (settings['bar-chart-data-value-columns'].length === 1) {
        result.valueColumns = settings['bar-chart-data-value-columns'][0]
      } else {
        result.valueColumns = settings['bar-chart-data-value-columns']
      }
    }

    // Heading
    if (settings['bar-chart-data-heading']) {
      const heading = formatFromVariableText(settings['bar-chart-data-heading'])
      if (heading.length > 0 && heading[0] !== 'text:') {
        result.heading = heading
      }
    }

    // Sort By
    if (settings['bar-chart-data-sort-by']) {
      result.sortBy = settings['bar-chart-data-sort-by']
    }

    // Sort Type
    if (settings['bar-chart-data-sort-type']) {
      result.sortType = settings['bar-chart-data-sort-type']
    }

    // Filter
    if (settings['bar-chart-data-filter']) {
      result.filter = settings['bar-chart-data-filter']
    }

    // Items Per Page
    if (settings['bar-chart-data-items-per-page'] && settings['bar-chart-data-items-per-page'].length > 0) {
      result.itemsPerPage = settings['bar-chart-data-items-per-page'][0]
    }

    // Width
    const width = formatWidthFormat(settings['bar-chart-design-width'])
    if (width) {
      result.width = width
    }

    // Alignment
    if (settings['bar-chart-design-alignment']) {
      const alignment = formatComponentAlignmentFormat(settings['bar-chart-design-alignment'] as any)
      if (alignment) {
        result.alignment = alignment
      }
    }

    // Heading Alignment
    if (settings['bar-chart-design-heading-alignment']) {
      const headingAlignment = formatComponentAlignmentFormat(settings['bar-chart-design-heading-alignment'] as any)
      if (headingAlignment) {
        result.headingAlignment = headingAlignment
      }
    }

    // Margin
    const margin = formatSpacingFormat(settings['bar-chart-design-comp-margin'])
    if (margin) {
      result.margin = margin
    }

    // Heading Margin
    const headingMargin = formatSpacingFormat(settings['bar-chart-design-heading-margin'])
    if (headingMargin) {
      result.headingMargin = headingMargin
    }

    // Typography
    const typography = formatTypographyFormat(settings['bar-chart-design-typography'])
    if (typography) {
      result.typography = typography
    }

    // Heading Typography
    if (settings['bar-chart-design-heading-typography']) {
      const headingTypo = formatTypographyFormat({
        phone: settings['bar-chart-design-heading-typography'],
        'phone-landscape': settings['bar-chart-design-heading-typography'],
        tablet: settings['bar-chart-design-heading-typography'],
        'tablet-landscape': settings['bar-chart-design-heading-typography'],
        desktop: settings['bar-chart-design-heading-typography'],
        tv: settings['bar-chart-design-heading-typography'],
      } as any)
      if (headingTypo) {
        result.headingTypography = headingTypo
      }
    }

    // Color Sequence
    if (settings['bar-chart-design-color-sequence'] && settings['bar-chart-design-color-sequence'] !== template['bar-chart-design-color-sequence']) {
      result.colorSequence = settings['bar-chart-design-color-sequence']
    }

    // Legend
    if (settings['bar-chart-design-legend'] !== undefined && settings['bar-chart-design-legend'] !== template['bar-chart-design-legend']) {
      result.legend = settings['bar-chart-design-legend']
    }

    // Show Grid
    if (settings['bar-chart-design-show-grid'] !== undefined && settings['bar-chart-design-show-grid'] !== template['bar-chart-design-show-grid']) {
      result.showGrid = settings['bar-chart-design-show-grid']
    }

    // Show Axis
    if (settings['bar-chart-design-show-axis'] !== undefined && settings['bar-chart-design-show-axis'] !== template['bar-chart-design-show-axis']) {
      result.showAxis = settings['bar-chart-design-show-axis']
    }

    // Horizontal
    if (settings['bar-chart-design-horizontal'] && settings['bar-chart-design-horizontal'] !== template['bar-chart-design-horizontal']) {
      result.horizontal = settings['bar-chart-design-horizontal']
    }

    // Style
    if (settings['bar-chart-design-style'] && settings['bar-chart-design-style'] !== template['bar-chart-design-style']) {
      result.style = settings['bar-chart-design-style']
    }

    return result
  }

  /**
   * parse AI bar chart input to bar chart component settings
   */
  parse(input: AiBarChartInput): IBarChartComponentSettings {
    const result = deepMerge(BAR_CHART_DEFAULT_TEMPLATE, {})

    // Data - Source (required)
    if (input.dataSource) {
      result['bar-chart-data-source'] = input.dataSource
    }

    // Data - X Axis Column
    if (input.xAxisColumn) {
      result['bar-chart-data-x-axis-column'] = input.xAxisColumn
    }

    // Data - Value Columns
    if (input.valueColumns) {
      if (Array.isArray(input.valueColumns)) {
        result['bar-chart-data-value-columns'] = input.valueColumns
      } else {
        result['bar-chart-data-value-columns'] = [input.valueColumns]
      }
    }

    // Data - Heading
    if (input.heading !== undefined) {
      result['bar-chart-data-heading'] = parseToVariableText(input.heading)
    }

    // Data - Sort By
    if (input.sortBy) {
      result['bar-chart-data-sort-by'] = input.sortBy
    }

    // Data - Sort Type
    if (input.sortType) {
      result['bar-chart-data-sort-type'] = input.sortType
    }

    // Data - Filter
    if (input.filter) {
      result['bar-chart-data-filter'] = input.filter
    }

    // Data - Items Per Page
    if (input.itemsPerPage !== undefined) {
      result['bar-chart-data-items-per-page'] = [input.itemsPerPage]
    }

    // Design - Width
    if (input.width) {
      result['bar-chart-design-width'] = parseWidthFormat(input.width)
    }

    // Design - Alignment
    if (input.alignment) {
      const alignmentMap: Record<string, string> = {
        left: 'left',
        center: 'center',
        right: 'right',
        parent: 'parent',
      }
      const alignment = alignmentMap[input.alignment] || 'parent'
      result['bar-chart-design-alignment'] = {
        phone: alignment,
        'phone-landscape': alignment,
        tablet: alignment,
        'tablet-landscape': alignment,
        desktop: alignment,
        tv: alignment,
      } as ResponsiveValue<string>
    }

    // Design - Heading Alignment
    if (input.headingAlignment) {
      const alignmentMap: Record<string, string> = {
        left: 'left',
        center: 'center',
        right: 'right',
        parent: 'parent',
      }
      const alignment = alignmentMap[input.headingAlignment] || 'center'
      result['bar-chart-design-heading-alignment'] = {
        phone: alignment,
        'phone-landscape': alignment,
        tablet: alignment,
        'tablet-landscape': alignment,
        desktop: alignment,
        tv: alignment,
      } as ResponsiveValue<string>
    }

    // Design - Margin
    if (input.margin) {
      result['bar-chart-design-comp-margin'] = parseSpacingFormat(input.margin)
    }

    // Design - Heading Margin
    if (input.headingMargin) {
      result['bar-chart-design-heading-margin'] = parseSpacingFormat(input.headingMargin)
    }

    // Design - Typography
    if (input.typography) {
      result['bar-chart-design-typography'] = parseTypographyFormat(input.typography)
    }

    // Design - Heading Typography
    if (input.headingTypography) {
      const typo = parseTypographyFormat(input.headingTypography)
      // Apply to heading typography (single TypographyValue, not ResponsiveValue)
      const firstBreakpoint = Object.values(typo)[0]
      if (firstBreakpoint) {
        result['bar-chart-design-heading-typography'] = firstBreakpoint
      }
    }

    // Design - Color Sequence
    if (input.colorSequence) {
      result['bar-chart-design-color-sequence'] = input.colorSequence
    }

    // Design - Legend
    if (input.legend !== undefined) {
      result['bar-chart-design-legend'] = input.legend
    }

    // Design - Show Grid
    if (input.showGrid !== undefined) {
      result['bar-chart-design-show-grid'] = input.showGrid
    }

    // Design - Show Axis
    if (input.showAxis !== undefined) {
      result['bar-chart-design-show-axis'] = input.showAxis
    }

    // Design - Horizontal
    if (input.horizontal) {
      result['bar-chart-design-horizontal'] = input.horizontal
    }

    // Design - Style
    if (input.style) {
      result['bar-chart-design-style'] = input.style
    }

    return result
  }
}

